import { useEffect, useRef, useState } from "react";
import { Plus, Trash, PencilSimple, X, Play, Pause, Microphone, Stop, UploadSimple, SpeakerSimpleX, CaretRight, FolderSimple, FolderPlus, DownloadSimple, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CATS = [["tahfidz", "Tahfidz"], ["doa", "Doa"], ["hadits", "Hadits"], ["arab", "B. Arab"], ["inggris", "B. Inggris"], ["lainnya", "Lainnya"]];
const FIELDS = {
  doa: [["label", "Nama Doa", "text"], ["arabic", "Teks Arab", "arabic"], ["latin", "Teks Latin", "text"], ["translation", "Terjemahan", "textarea"], ["source", "Sumber (opsional)", "text"]],
  hadits: [["label", "Judul Hadits", "text"], ["arabic", "Teks Arab", "arabic"], ["latin", "Teks Latin", "text"], ["translation", "Terjemahan", "textarea"], ["source", "Sumber (opsional)", "text"]],
  arab: [["arabic", "Kata Arab", "arabic"], ["latin", "Tulisan Latin", "text"], ["meaning", "Arti Indonesia", "text"]],
  inggris: [["label", "Kata Inggris", "text"], ["meaning", "Arti Indonesia", "text"], ["example", "Contoh Kalimat", "textarea"]],
  lainnya: [["label", "Judul", "text"], ["description", "Deskripsi", "text"], ["translation", "Teks", "textarea"]],
};

export default function KontenHafalan() {
  const [tab, setTab] = useState("doa");
  const [custom, setCustom] = useState([]);
  const loadCats = () => api.get("/content-categories").then((r) => setCustom(r.data || [])).catch(() => {});
  useEffect(() => { loadCats(); }, []);
  const merged = [...CATS.filter((c) => c[0] !== "lainnya"), ...custom.map((c) => [c.key, c.title]), ["lainnya", "Lainnya"]];
  const catLabel = merged.find((c) => c[0] === tab)?.[1];
  const addCategory = async () => {
    const title = window.prompt("Nama kategori baru (mis. Lagu Islami, Hafalan Hadits):");
    if (!title || !title.trim()) return;
    try { const { data } = await api.post("/content-categories", { title: title.trim() }); await loadCats(); setTab(data.key); toast.success("Kategori ditambahkan"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [manageOpen, setManageOpen] = useState(false);
  const renameCat = async (c) => {
    const t = window.prompt("Ubah nama kategori:", c.title);
    if (!t || !t.trim()) return;
    try { await api.put(`/content-categories/${c.key}`, { title: t.trim() }); await loadCats(); toast.success("Nama kategori diperbarui"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteCat = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.title}" beserta isinya?`)) return;
    try { await api.delete(`/content-categories/${c.key}`); if (tab === c.key) setTab("doa"); await loadCats(); toast.success("Kategori dihapus"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const moveCat = async (idx, dir) => {
    const arr = custom.map((c) => c.key); const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.put("/content-categories-reorder", { keys: arr }); await loadCats(); }
    catch (e) { toast.error("Gagal mengurutkan"); }
  };
  return (
    <div>
      <PageHeader title="Kelola Konten Hafalan" />
      <div className="px-5 pt-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
          {merged.map(([v, l]) => (
            <button key={v} data-testid={`konten-tab-${v}`} onClick={() => setTab(v)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold ${tab === v ? "bg-primary text-white shadow" : "bg-white border border-stone-200 text-stone-500"}`}>{l}</button>
          ))}
          <button data-testid="konten-add-category" onClick={addCategory}
            className="shrink-0 px-4 py-2 rounded-full text-sm font-semibold bg-white border-2 border-dashed border-primary/40 text-primary">+ Kategori</button>
          {custom.length > 0 && (
            <button data-testid="konten-manage-category" onClick={() => setManageOpen(true)}
              className="shrink-0 px-4 py-2 rounded-full text-sm font-semibold bg-white border border-stone-200 text-stone-500">Kelola</button>
          )}
        </div>
        {tab === "tahfidz" ? <TahfidzAudio /> : <CategoryContent key={tab} tab={tab} label={catLabel} />}
        {manageOpen && (
          <div className="fixed inset-0 z-[65] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setManageOpen(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[80vh] overflow-y-auto" data-testid="konten-manage-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-lg text-stone-800">Kelola Kategori</h3>
                <button onClick={() => setManageOpen(false)}><X size={22} className="text-stone-400" /></button>
              </div>
              <div className="space-y-2">
                {custom.map((c, idx) => (
                  <div key={c.key} data-testid={`manage-cat-${c.key}`} className="flex items-center gap-2 bg-stone-50 rounded-xl p-2.5">
                    <span className="flex-1 font-semibold text-stone-700 text-sm truncate">{c.title}</span>
                    <button data-testid={`cat-up-${c.key}`} disabled={idx === 0} onClick={() => moveCat(idx, -1)} className="w-8 h-8 rounded-lg bg-white border border-stone-200 text-stone-600 disabled:opacity-30 font-bold">↑</button>
                    <button data-testid={`cat-down-${c.key}`} disabled={idx === custom.length - 1} onClick={() => moveCat(idx, 1)} className="w-8 h-8 rounded-lg bg-white border border-stone-200 text-stone-600 disabled:opacity-30 font-bold">↓</button>
                    <button data-testid={`cat-rename-${c.key}`} onClick={() => renameCat(c)} className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><PencilSimple size={15} weight="bold" /></button>
                    <button data-testid={`cat-del-${c.key}`} onClick={() => deleteCat(c)} className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><Trash size={15} weight="bold" /></button>
                  </div>
                ))}
                {custom.length === 0 && <p className="text-center text-stone-400 text-sm py-4">Belum ada kategori tambahan.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Audio hooks (shared) ----------
function useRecorder(onFile) {
  const [recId, setRecId] = useState(null);
  const recRef = useRef(null); const chunksRef = useRef([]);
  const start = async (id) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        await onFile(id, new File([blob], "rekaman.webm", { type: blob.type })); setRecId(null);
      };
      mr.start(); recRef.current = mr; setRecId(id);
    } catch (e) { toast.error("Tidak dapat mengakses mikrofon"); }
  };
  const stop = () => recRef.current?.stop();
  return { recId, start, stop };
}

function AudioControls({ hasAudio, playing, onPlay, recActive, onRec, onStopRec, onUpload, onDelete, testid }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {hasAudio ? (
        <>
          <button data-testid={`${testid}-play`} onClick={onPlay} className="px-3 py-1.5 rounded-full bg-primary text-white text-xs font-semibold flex items-center gap-1 active:scale-95 transition-transform">
            {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />} {playing ? "Jeda" : "Putar"}
          </button>
          <button data-testid={`${testid}-audio-del`} onClick={onDelete} className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-500 text-xs font-semibold flex items-center gap-1"><SpeakerSimpleX size={14} weight="fill" /> Hapus</button>
        </>
      ) : <span className="text-xs text-stone-400">Belum ada audio</span>}
      {recActive ? (
        <button data-testid={`${testid}-rec-stop`} onClick={onStopRec} className="px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center gap-1 animate-pulse"><Stop size={14} weight="fill" /> Stop</button>
      ) : (
        <button data-testid={`${testid}-rec`} onClick={onRec} className="px-3 py-1.5 rounded-full bg-rose-100 text-rose-600 text-xs font-semibold flex items-center gap-1"><Microphone size={14} weight="fill" /> Rekam</button>
      )}
      <label className="px-3 py-1.5 rounded-full bg-sky-100 text-sky-600 text-xs font-semibold flex items-center gap-1 cursor-pointer">
        <UploadSimple size={14} weight="bold" /> Upload
        <input data-testid={`${testid}-upload`} type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/webm,audio/ogg" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
      </label>
    </div>
  );
}

// ---------- Category content with nested folders ----------
function CategoryContent({ tab, label }) {
  const catLabel = label || CATS.find(([v]) => v === tab)?.[1] || "Konten";
  const fields = FIELDS[tab] || FIELDS.lainnya;
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [path, setPath] = useState([]);
  const folder = path[path.length - 1] || null;
  const currentId = folder?.id || null;

  const load = () => {
    api.get(`/content-folders/${tab}`).then((r) => setFolders(r.data));
    api.get(`/content/${tab}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); setPath([]); }, [tab]); // eslint-disable-line

  const addFolder = async () => {
    const title = window.prompt("Nama Folder baru:");
    if (!title || !title.trim()) return;
    try { await api.post(`/content-folders/${tab}`, { title, parent_id: currentId }); toast.success("Folder ditambahkan"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const renameFolder = async (f) => {
    const title = window.prompt("Ubah nama folder:", f.title);
    if (!title || !title.trim()) return;
    await api.put(`/content-folders/${f.id}`, { title }); load();
    setPath((p) => p.map((x) => (x.id === f.id ? { ...x, title } : x)));
  };
  const delFolder = async (f) => {
    if (!window.confirm(`Hapus folder "${f.title}" beserta isinya (termasuk sub-folder)?`)) return;
    await api.delete(`/content-folders/${f.id}`); toast.success("Folder dihapus"); load();
  };
  const reorderFolder = async (idx, dir) => {
    const arr = [...subFolders]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setFolders((prev) => { const others = prev.filter((x) => (x.parent_id || null) !== currentId); return [...others, ...arr]; });
    try { await api.put("/content-folders-reorder", { ids: arr.map((f) => f.id) }); load(); }
    catch { toast.error("Gagal mengurutkan folder"); load(); }
  };
  const reorderItem = async (idx, dir) => {
    const arr = [...visibleItems]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems((prev) => { const others = prev.filter((x) => (x.folder_id || null) !== currentId); return [...others, ...arr]; });
    try { await api.put("/content-reorder", { ids: arr.map((x) => x.id) }); load(); }
    catch { toast.error("Gagal mengurutkan"); load(); }
  };

  // item modal
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const save = async () => {
    const body = {}; fields.forEach(([k]) => { body[k] = form[k] || null; });
    if (!body.label) body.label = body.arabic || body.latin || body.meaning || "Item";
    if (!form[fields[0][0]]) { toast.error(`${fields[0][1]} wajib diisi`); return; }
    try {
      if (editId) await api.put(`/content/${editId}`, body);
      else await api.post(`/content/${tab}${currentId ? `?folder_id=${currentId}` : ""}`, body);
      toast.success("Konten tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus item ini?")) return; await api.delete(`/content/${id}`); load(); };

  // move item to a folder (or root)
  const [moveItem, setMoveItem] = useState(null);
  const moveTo = async (fid) => { try { await api.put(`/content/${moveItem.id}`, { folder_id: fid }); toast.success("Item dipindahkan"); setMoveItem(null); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const byId = Object.fromEntries(folders.map((x) => [x.id, x]));
  const folderDepth = (f) => { let d = 0, p = f.parent_id; while (p) { d++; p = byId[p]?.parent_id; } return d; };

  // audio
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const stopA = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setPlaying(null); };
  const toggle = (it) => { if (playing === it.id) { stopA(); return; } stopA(); const a = new Audio(it.audio); a.onended = () => setPlaying(null); a.play().catch(() => {}); audioRef.current = a; setPlaying(it.id); };
  const uploadAudio = async (id, file) => { const fd = new FormData(); fd.append("file", file); try { await api.post(`/content/${id}/audio`, fd); toast.success("Audio tersimpan"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const delAudio = async (id) => { if (!window.confirm("Yakin mau dihapus?")) return; await api.delete(`/content/${id}/audio`); load(); };
  const rec = useRecorder(uploadAudio);

  // import / export folder items
  const doImport = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f || !folder) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post(`/content-folders/${folder.id}/import`, fd); toast.success(`${data.created} item diimport`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const doExport = async () => {
    if (!folder) return;
    try {
      const res = await api.get(`/content-folders/${folder.id}/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `${folder.title}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengekspor"); }
  };

  const title = (it) => it.label || it.arabic || it.latin || "Item";
  const subFolders = folders.filter((f) => (f.parent_id || null) === currentId);
  const visibleItems = items.filter((it) => (it.folder_id || null) === currentId);

  return (
    <>
      {path.length > 0 && (
        <div className="flex items-center gap-1 mb-3 text-sm flex-wrap" data-testid="konten-breadcrumb">
          <button onClick={() => { stopA(); setPath([]); }} className="text-primary font-semibold">Root</button>
          {path.map((f, i) => (
            <span key={f.id} className="flex items-center gap-1">
              <CaretRight size={12} className="text-stone-300" />
              <button data-testid="konten-folder-back" onClick={() => setPath(path.slice(0, i + 1))} className={`font-semibold ${i === path.length - 1 ? "text-stone-800" : "text-primary"}`}>{f.title}</button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-3">
        <button data-testid="konten-add-folder-btn" onClick={addFolder}
          className="flex-1 border-2 border-dashed border-primary/40 text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <FolderPlus size={18} weight="bold" /> Tambah Folder
        </button>
        <button data-testid="konten-add-btn" onClick={() => { setForm({}); setEditId(null); setOpen(true); }}
          className="flex-1 border-2 border-dashed border-primary/40 text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <Plus size={18} weight="bold" /> Tambah {catLabel}
        </button>
      </div>

      {folder && (
        <div className="flex gap-2 mb-3">
          <label data-testid="konten-import-btn" className="flex-1 bg-sky-100 text-sky-600 text-xs font-semibold py-2.5 rounded-xl flex items-center justify-center gap-1 cursor-pointer"><UploadSimple size={15} weight="bold" /> Import<input type="file" accept=".xlsx,.csv" className="hidden" onChange={doImport} /></label>
          <button data-testid="konten-export-btn" onClick={doExport} className="flex-1 bg-emerald-100 text-emerald-600 text-xs font-semibold py-2.5 rounded-xl flex items-center justify-center gap-1"><DownloadSimple size={15} weight="bold" /> Export</button>
        </div>
      )}

      {subFolders.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="konten-folders">
          {subFolders.map((f, idx) => (
            <div key={f.id} data-testid={`konten-folder-${f.id}`} className="bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center gap-3 shadow-sm">
              <button onClick={() => { stopA(); setPath([...path, f]); }} className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.98] transition-transform">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><FolderSimple size={20} weight="fill" className="text-amber-500" /></div>
                <p className="font-semibold text-stone-800 text-sm truncate flex-1">{f.title}</p>
                <CaretRight size={16} className="text-stone-300 shrink-0" />
              </button>
              <button data-testid={`konten-folder-up-${f.id}`} disabled={idx === 0} onClick={() => reorderFolder(idx, -1)} className="text-stone-400 disabled:opacity-25 hover:text-primary"><ArrowUp size={16} weight="bold" /></button>
              <button data-testid={`konten-folder-down-${f.id}`} disabled={idx === subFolders.length - 1} onClick={() => reorderFolder(idx, 1)} className="text-stone-400 disabled:opacity-25 hover:text-primary"><ArrowDown size={16} weight="bold" /></button>
              <button data-testid={`konten-folder-edit-${f.id}`} onClick={() => renameFolder(f)} className="text-stone-400 hover:text-primary"><PencilSimple size={16} /></button>
              <button data-testid={`konten-folder-del-${f.id}`} onClick={() => delFolder(f)} className="text-stone-400 hover:text-destructive"><Trash size={16} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3 pb-6">
        {visibleItems.map((it, idx) => (
          <div key={it.id} data-testid={`konten-item-${it.id}`} className="bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="font-semibold text-stone-800 text-sm flex-1"><span className="text-primary mr-1">{idx + 1}.</span>{title(it)}</p>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`konten-up-${it.id}`} disabled={idx === 0} onClick={() => reorderItem(idx, -1)} title="Naik" className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 disabled:opacity-25 hover:text-primary"><ArrowUp size={15} weight="bold" /></button>
                <button data-testid={`konten-down-${it.id}`} disabled={idx === visibleItems.length - 1} onClick={() => reorderItem(idx, 1)} title="Turun" className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 disabled:opacity-25 hover:text-primary"><ArrowDown size={15} weight="bold" /></button>
                <button data-testid={`konten-move-${it.id}`} onClick={() => setMoveItem(it)} title="Pindahkan ke folder" className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:text-amber-500"><FolderSimple size={15} weight="bold" /></button>
                <button data-testid={`konten-edit-${it.id}`} onClick={() => { setForm({ ...it }); setEditId(it.id); setOpen(true); }} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:text-primary"><PencilSimple size={15} weight="bold" /></button>
                <button data-testid={`konten-del-${it.id}`} onClick={() => del(it.id)} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:text-destructive"><Trash size={15} weight="bold" /></button>
              </div>
            </div>
            {it.arabic && <p className="font-arabic text-2xl text-right text-stone-800 leading-loose">{it.arabic}</p>}
            {it.latin && <p className="text-primary/80 italic text-xs">{it.latin}</p>}
            {it.meaning && <p className="text-stone-600 text-xs">{it.meaning}</p>}
            {it.description && <p className="text-stone-600 text-xs mt-0.5">{it.description}</p>}
            {it.translation && <p className="text-stone-600 text-xs mt-0.5">{it.translation}</p>}
            {it.example && <p className="text-stone-500 text-xs italic mt-0.5">"{it.example}"</p>}
            <div className="mt-3 pt-3 border-t border-stone-100">
              <AudioControls testid={`konten-${it.id}`} hasAudio={!!it.audio} playing={playing === it.id} onPlay={() => toggle(it)}
                recActive={rec.recId === it.id} onRec={() => rec.start(it.id)} onStopRec={rec.stop}
                onUpload={(f) => uploadAudio(it.id, f)} onDelete={() => delAudio(it.id)} />
            </div>
          </div>
        ))}
        {visibleItems.length === 0 && subFolders.length === 0 && <p className="text-center text-stone-400 mt-6">{folder ? "Folder ini masih kosong." : "Belum ada konten di luar folder."}</p>}
      </div>

      {moveItem && (
        <div className="fixed inset-0 z-[65] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setMoveItem(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Pindahkan ke Folder</h3>
              <button onClick={() => setMoveItem(null)}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="space-y-2">
              <button data-testid="move-to-root" onClick={() => moveTo(null)} className="w-full text-left bg-stone-50 rounded-xl p-3.5 flex items-center gap-3 active:scale-[0.98] transition-transform">
                <div className="w-9 h-9 rounded-lg bg-stone-200 flex items-center justify-center"><X size={16} weight="bold" className="text-stone-500" /></div>
                <span className="font-semibold text-stone-700 text-sm">Tanpa Folder (root)</span>
              </button>
              {folders.map((f) => (
                <button key={f.id} data-testid={`move-to-${f.id}`} onClick={() => moveTo(f.id)} style={{ paddingLeft: 14 + folderDepth(f) * 18 }}
                  className={`w-full text-left rounded-xl p-3.5 flex items-center gap-3 active:scale-[0.98] transition-transform ${moveItem.folder_id === f.id ? "bg-primary/10 border border-primary/30" : "bg-stone-50"}`}>
                  <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><FolderSimple size={16} weight="fill" className="text-amber-500" /></div>
                  <span className="font-semibold text-stone-700 text-sm flex-1">{f.title}</span>
                  {moveItem.folder_id === f.id && <span className="text-[11px] text-primary font-semibold">saat ini</span>}
                </button>
              ))}
              {folders.length === 0 && <p className="text-center text-stone-400 text-sm py-4">Belum ada folder. Buat folder dulu.</p>}
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">{editId ? "Edit" : "Tambah"} {catLabel}</h3>
              <button onClick={() => setOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            {fields.map(([k, label, type]) => (
              <div key={k} className="mb-3">
                <p className="text-xs text-stone-500 mb-1">{label}</p>
                {type === "textarea" ? (
                  <textarea data-testid={`konten-field-${k}`} rows={3} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 outline-none focus:border-primary" />
                ) : (
                  <input data-testid={`konten-field-${k}`} type="text" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className={`w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 outline-none focus:border-primary ${type === "arabic" ? "font-arabic text-2xl text-right leading-loose" : ""}`} />
                )}
              </div>
            ))}
            <button data-testid="konten-save-btn" onClick={save} className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform mt-1">Simpan</button>
          </div>
        </div>
      )}
    </>
  );
}

// ---------- Tahfidz per-ayat audio (juz-aware for SDQu) ----------
function TahfidzAudio() {
  const { user } = useAuth();
  const isSdqu = user?.school === "sdqu";
  const JUZ = [30, 29, 28, 27, 26];
  const [juz, setJuz] = useState(30);
  const [surah, setSurah] = useState([]);
  const [sel, setSel] = useState(null);
  const [audioMap, setAudioMap] = useState({});
  const [ayatText, setAyatText] = useState([]);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => { api.get(`/tahfidz/surah${isSdqu ? `?juz=${juz}` : ""}`).then((r) => setSurah(r.data)); }, [juz, isSdqu]);
  const loadAudio = (num) => api.get(`/tahfidz/audio?surah=${num}`).then((r) => setAudioMap(r.data));
  useEffect(() => {
    if (!sel) return;
    loadAudio(sel.num); setAyatText([]);
    fetch(`https://api.alquran.cloud/v1/surah/${sel.num}/editions/quran-uthmani,id.indonesian`).then((r) => r.json())
      .then((j) => { const ar = j.data?.[0]?.ayahs || []; const idn = j.data?.[1]?.ayahs || []; setAyatText(ar.map((a, i) => ({ n: a.numberInSurah, text: a.text, arti: idn[i]?.text }))); }).catch(() => {});
  }, [sel]);

  const stopA = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setPlaying(null); };
  const toggle = (ayat, url) => { if (playing === ayat) { stopA(); return; } stopA(); const a = new Audio(url); a.onended = () => setPlaying(null); a.play().catch(() => {}); audioRef.current = a; setPlaying(ayat); };
  const uploadAudio = async (ayat, file) => { const fd = new FormData(); fd.append("file", file); try { await api.post(`/tahfidz/audio?surah=${sel.num}&ayat=${ayat}`, fd); toast.success(`Audio ayat ${ayat} tersimpan`); loadAudio(sel.num); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const delAudio = async (ayat) => { if (!window.confirm("Yakin mau dihapus?")) return; await api.delete(`/tahfidz/audio?surah=${sel.num}&ayat=${ayat}`); loadAudio(sel.num); };
  const rec = useRecorder(uploadAudio);

  if (!sel) {
    return (
      <div className="pb-6">
        {isSdqu && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3" data-testid="tahfidz-juz-tabs">
            {JUZ.map((j) => (
              <button key={j} data-testid={`tahfidz-juz-${j}`} onClick={() => setJuz(j)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold ${juz === j ? "bg-primary text-white shadow" : "bg-white border border-stone-200 text-stone-500"}`}>Juz {j}</button>
            ))}
          </div>
        )}
        <div className="space-y-2" data-testid="tahfidz-surah-list">
          {surah.map((s) => (
            <button key={s.num} data-testid={`tahfidz-surah-${s.num}`} onClick={() => setSel(s)}
              className="w-full bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center gap-3 shadow-sm active:scale-[0.98] transition-transform">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><span className="text-primary font-bold text-xs">{s.num}</span></div>
              <div className="flex-1 text-left"><p className="font-semibold text-stone-800 text-sm">{s.name}</p><p className="text-stone-400 text-xs">{s.ayat} ayat</p></div>
              <CaretRight size={18} className="text-stone-300" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <button data-testid="tahfidz-back" onClick={() => { stopA(); setSel(null); }} className="text-stone-500 text-sm font-semibold mb-3">← Semua Surah</button>
      <p className="font-heading font-bold text-stone-800 mb-3">{sel.name} • {sel.ayat} ayat</p>
      <div className="space-y-2">
        {(ayatText.length ? ayatText : Array.from({ length: sel.ayat }).map((_, i) => ({ n: i + 1, text: null }))).map((a) => {
          const ayat = a.n; const url = audioMap[String(ayat)];
          return (
            <div key={ayat} data-testid={`tahfidz-ayat-${ayat}`} className="bg-white rounded-2xl border border-stone-200/60 p-3 shadow-sm">
              <div className="flex items-start gap-2 mb-2">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center shrink-0 mt-1">{ayat}</span>
                {a.text ? <p className="font-arabic text-2xl leading-loose text-right flex-1 text-stone-800" dir="rtl">{a.text}</p> : <p className="flex-1 text-stone-500 text-sm font-semibold">Ayat {ayat}</p>}
              </div>
              {a.arti && <p className="text-stone-600 text-xs mb-2 leading-relaxed">{a.arti}</p>}
              <AudioControls testid={`tahfidz-${ayat}`} hasAudio={!!url} playing={playing === ayat} onPlay={() => toggle(ayat, url)}
                recActive={rec.recId === ayat} onRec={() => rec.start(ayat)} onStopRec={rec.stop}
                onUpload={(f) => uploadAudio(ayat, f)} onDelete={() => delAudio(ayat)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
